import { validateDateRange } from "./upcoming-earnings-data-source.js";

const PAGE_SIZE = 100;
const MAX_PAGES = 200;
const SNAPSHOT_FIELDS = [
  "snapshotId", "capturedAtUtc", "source", "archiveManifestSha256", "sourceUrl", "importedAtUtc",
  "rowCount", "matchedRowCount", "unresolvedRowCount", "invalidRowCount", "duplicateRowCount", "conflictRowCount",
  "issuerObservedCount", "issuerMatchedCount", "issuerConflictCount", "issuerDuplicateCount", "providerOverlapCount"
];
const SORT_KEYS = new Set(["date", "symbol", "type", "ratio", "status", "source"]);
const COUNT_FIELDS = ["rowCount", "matchedRowCount", "unresolvedRowCount", "invalidRowCount", "duplicateRowCount", "conflictRowCount"];
const ISSUER_COUNT_FIELDS = ["issuerObservedCount", "issuerMatchedCount", "issuerConflictCount", "issuerDuplicateCount"];

export class UpcomingSplitsDataSource {
  constructor({ endpoint = "/api/v1/upcoming-splits", fetchImpl = globalThis.fetch } = {}) {
    this.endpoint = endpoint;
    this.fetchImpl = fetchImpl.bind(globalThis);
  }

  async load({ from, to, signal, onProgress = () => {} }) {
    validateDateRange(from, to);
    const items = [];
    const seenIds = new Set();
    const seenSymbolDates = new Set();
    let offset = 0;
    let metadata = null;
    let fingerprint = null;
    for (let pageNumber = 0; pageNumber < MAX_PAGES; pageNumber += 1) {
      signal?.throwIfAborted();
      const url = new URL(this.endpoint, globalThis.location?.href || "http://localhost/");
      url.searchParams.set("from", from); url.searchParams.set("to", to);
      url.searchParams.set("offset", String(offset)); url.searchParams.set("limit", String(PAGE_SIZE));
      const response = await this.fetchImpl(url.toString(), {
        signal, credentials: "same-origin", headers: { Accept: "application/json" }
      });
      signal?.throwIfAborted();
      if (!response.ok) throw new Error(response.status === 401 || response.status === 403
        ? "Sign in to view upcoming splits." : "Upcoming splits could not be loaded. Please try again.");
      let page;
      try { page = await response.json(); } catch { throw incomplete(); }
      signal?.throwIfAborted();
      if (!page || !Array.isArray(page.items) || !Number.isSafeInteger(page.totalCount)
        || page.totalCount < 0 || page.offset !== offset || page.items.length > PAGE_SIZE
        || page.from !== from || page.to !== to
        || COUNT_FIELDS.some((key) => !Number.isSafeInteger(page[key]) || page[key] < 0)
        || !(page.snapshotId === null || typeof page.snapshotId === "string" && page.snapshotId)
        || page.snapshotId !== null && !validTimestamp(page.capturedAtUtc)) throw incomplete();
      const currentFingerprint = JSON.stringify(SNAPSHOT_FIELDS.map((key) => page[key] ?? null));
      if (metadata && (page.totalCount !== metadata.totalCount || currentFingerprint !== fingerprint)) {
        throw new Error("The split snapshot changed while loading. Please try again.");
      }
      if (!validMetadata(page)) throw incomplete();
      if (!metadata) {
        const { items: ignored, ...summary } = page;
        metadata = summary; fingerprint = currentFingerprint;
      }
      for (const item of page.items) {
        if (!validItem(item, from, to, page) || seenIds.has(item.id)
          || seenSymbolDates.has(`${item.symbolId}/${item.effectiveDate}`)) throw incomplete();
        seenIds.add(item.id); seenSymbolDates.add(`${item.symbolId}/${item.effectiveDate}`);
        items.push(item);
      }
      if (items.length > page.totalCount) throw incomplete();
      onProgress({ loaded: items.length, total: page.totalCount });
      if (page.nextOffset === null) {
        if (items.length !== page.totalCount) throw incomplete();
        return { items, metadata };
      }
      if (!Number.isSafeInteger(page.nextOffset) || page.nextOffset !== offset + page.items.length
        || page.items.length === 0 || page.nextOffset >= page.totalCount) throw incomplete();
      offset = page.nextOffset;
    }
    throw new Error("This date range has too many results. Choose a shorter date range.");
  }
}

export function filterAndSortSplits(items, {
  query = "", type = "all", sortKey = "date", sortDirection = "asc"
} = {}) {
  const search = String(query).trim().toLocaleLowerCase();
  const key = SORT_KEYS.has(sortKey) ? sortKey : "date";
  const direction = sortDirection === "desc" ? -1 : 1;
  const filtered = items.filter((item) => (type === "all" || item.splitType === type)
    && (!search || `${item.symbol} ${item.companyName}`.toLocaleLowerCase().includes(search)));
  const compare = (a, b) => String(a || "").localeCompare(String(b || ""), "en", { numeric: true });
  const value = (item) => ({ date: item.effectiveDate, symbol: item.symbol, type: item.splitType,
    ratio: item.splitFactor, status: item.status, source: item.source })[key];
  return filtered.sort((a, b) => {
    const primary = key === "ratio" ? Number(value(a)) - Number(value(b)) : compare(value(a), value(b));
    return primary * direction || compare(a.effectiveDate, b.effectiveDate)
      || compare(a.symbol, b.symbol) || compare(a.id, b.id);
  });
}

function validMetadata(page) {
  const issuerCount = (key) => page[key] ?? 0;
  if (!["TipRanks", "Mixed", "Issuer"].includes(page.source)
    || ISSUER_COUNT_FIELDS.some((key) => !Number.isSafeInteger(issuerCount(key)) || issuerCount(key) < 0)
    || ISSUER_COUNT_FIELDS.slice(1).reduce((sum, key) => sum + issuerCount(key), 0) > issuerCount("issuerObservedCount")
    || !Number.isSafeInteger(page.providerOverlapCount ?? 0) || (page.providerOverlapCount ?? 0) < 0
    || (page.providerOverlapCount ?? 0) > page.matchedRowCount
    || page.source === "TipRanks" && issuerCount("issuerObservedCount") !== 0
    || page.source !== "TipRanks" && issuerCount("issuerObservedCount") === 0) return false;
  if (page.snapshotId === null) return page.source !== "Mixed"
    && page.capturedAtUtc == null && page.importedAtUtc == null && page.archiveManifestSha256 == null && page.sourceUrl == null
    && COUNT_FIELDS.every((key) => page[key] === 0)
    && (page.source === "Issuer" || page.totalCount === 0);
  return page.source !== "Issuer";
}

function validItem(item, from, to, page) {
  if (!item || typeof item.id !== "string" || !item.id || typeof item.symbolId !== "string" || !item.symbolId
    || typeof item.symbol !== "string" || !item.symbol || typeof item.companyName !== "string"
    || !["Forward", "Reverse"].includes(item.splitType) || item.status !== "Announced"
    || !validProvenance(item, page)
    || ![item.numerator, item.denominator, item.splitFactor].every((value) => typeof value === "number" && Number.isFinite(value) && value > 0)) return false;
  if (item.splitType === "Forward" ? item.numerator <= item.denominator : item.numerator >= item.denominator) return false;
  try { validateDateRange(item.effectiveDate, item.effectiveDate); } catch { return false; }
  return item.effectiveDate >= from && item.effectiveDate <= to;
}
function validProvenance(item, page) {
  if (item.source === "TipRanks") return page.snapshotId !== null && page.source !== "Issuer"
    && item.verification === "ProviderReported";
  return item.source === "Issuer" && page.source !== "TipRanks" && item.verification === "PrimarySource"
    && validPublicUrl(item.sourceUrl) && typeof item.publisherName === "string" && item.publisherName.trim()
    && validTimestamp(item.sourceCapturedAtUtc) && (item.publishedAtUtc == null
      || validTimestamp(item.publishedAtUtc) && Date.parse(item.publishedAtUtc) <= Date.parse(item.sourceCapturedAtUtc))
    && ["RawHttp", "WebToolParsedExtract", "BrowserDomExtract"].includes(item.sourceContentKind)
    && typeof item.originalBodySha256 === "string" && /^[a-f\d]{64}$/i.test(item.originalBodySha256)
    && ["ExDate", "SplitAdjustedTradingDate"].includes(item.dateBasis)
    && typeof item.ratioNote === "string" && item.ratioNote.trim();
}
function validPublicUrl(value) {
  try {
    const url = new URL(value);
    return url.protocol === "https:" && !url.username && !url.password
      && !["localhost", "[::1]"].includes(url.hostname) && !url.hostname.endsWith(".local")
      && !url.hostname.endsWith(".internal") && !url.hostname.endsWith(".localhost")
      && !/^\d+(?:\.\d+){3}$/.test(url.hostname);
  } catch { return false; }
}
function validTimestamp(value) { return typeof value === "string" && Number.isFinite(Date.parse(value)); }
function incomplete() { return new Error("The split response was incomplete. Please try again."); }
